/**
 * ContextPanel — Sprint 2 shared primitive.
 *
 * Renders a collapsible side-rail card from a `ContextPanel` entry in the
 * approval's content_schema. The auto-renderer stacks these on the right
 * column of the card; the workflow author declares which fields to surface
 * via `panel.fields` (for kind='fields') or `panel.dataPath` (for other
 * kinds like 'thread', 'crm_card', 'doc_refs', 'chain', 'json').
 *
 * Sprint 2 ships the simple cases: 'fields', 'json', and a generic
 * collapsible wrapper for the others. Richer kind-specific UI (mini CRM
 * card, thread viewer) lands with the hand-designed renderers in Sprint
 * 10+ — for now the card surfaces the same data as a JSON preview so the
 * user is never left looking at an empty panel.
 */

import * as React from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { readPath } from '@argusai/shared';
import type { ContextPanel as ContextPanelDef } from '@argusai/shared';

interface ContextPanelProps {
  panel: ContextPanelDef;
  /** The full evidence object the panel reads from. */
  evidence: Record<string, unknown>;
  className?: string;
}

function asPretty(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

export function ContextPanel({
  panel,
  evidence,
  className,
}: ContextPanelProps) {
  const [open, setOpen] = React.useState(!panel.collapsedByDefault);
  const collapsible = panel.collapsible !== false;

  // Resolve the panel's data — either field-by-field for kind='fields' or
  // the root path for other kinds.
  let body: React.ReactNode;
  if (panel.kind === 'fields') {
    const rows = (panel.fields ?? []).map((f) => {
      const v = readPath(evidence, f.path);
      return (
        <div
          key={f.path}
          className="grid grid-cols-3 gap-2 py-1 text-xs"
          data-panel-field-path={f.path}
        >
          <dt className="col-span-1 text-muted-foreground">{f.label}</dt>
          <dd className="col-span-2 whitespace-pre-wrap break-words font-medium">
            {asPretty(v)}
          </dd>
        </div>
      );
    });
    body =
      rows.length > 0 ? (
        <dl className="divide-y divide-border/60">{rows}</dl>
      ) : (
        <p className="text-xs text-muted-foreground italic">
          No data to display
        </p>
      );
  } else {
    // kind in { crm_card, thread, doc_refs, chain, json, iframe } — for
    // Sprint 2 we render the resolved data as a JSON preview. Hand-designed
    // renderers in Sprint 10+ can specialize each kind.
    const data = panel.dataPath
      ? readPath(evidence, panel.dataPath)
      : evidence;
    body = (
      <pre className="max-h-72 overflow-auto whitespace-pre-wrap break-words rounded bg-muted/40 p-2 font-mono text-[10px]">
        {asPretty(data)}
      </pre>
    );
  }

  return (
    <section
      className={cn(
        'rounded-lg border border-border/80 bg-card',
        className,
      )}
      data-context-panel-id={panel.panelId}
      data-context-panel-kind={panel.kind}
    >
      <header className="flex items-center gap-2 border-b border-border/60 px-3 py-2">
        {collapsible ? (
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="flex items-center gap-1.5 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground"
            aria-expanded={open}
            aria-controls={`panel-body-${panel.panelId}`}
          >
            {open ? (
              <ChevronDown className="h-3 w-3" />
            ) : (
              <ChevronRight className="h-3 w-3" />
            )}
            <span>{panel.title}</span>
          </button>
        ) : (
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {panel.title}
          </span>
        )}
      </header>
      {open && (
        <div className="px-3 py-2" id={`panel-body-${panel.panelId}`}>
          {body}
        </div>
      )}
    </section>
  );
}
